import { useEffect, useMemo, useRef } from 'react';

import { fromKopecksToRouble } from '@scripts/helpers';

import { FilterValue, FormValues, PriceRange } from '../../../types';
import { getFieldName, isNumberArray, isPriceRangeDefault } from '../../../utils';

interface UsePriceRangeParams {
    currentFiltersData: { min_price?: number; max_price?: number } | null | undefined;
    isOpen: boolean;
    priceRange: PriceRange | undefined;
    rangeDefault: number[];
    formKey: string;
    rememberedPrice: number[] | undefined;
    values: FormValues;
    fieldPrefix: string;
    setFieldValue: (field: string, value: FilterValue | undefined) => void;
}

export const usePriceRange = ({
    currentFiltersData,
    isOpen,
    priceRange,
    rangeDefault,
    formKey,
    rememberedPrice,
    values,
    fieldPrefix,
    setFieldValue,
}: UsePriceRangeParams) => {
    const savedPriceRangeRef = useRef<{ min: number; max: number } | null>(null);
    const priceFieldName = getFieldName('price', fieldPrefix);

    const currentPriceValue = useMemo(() => {
        const priceValue = values[priceFieldName];

        return priceValue !== undefined && isNumberArray(priceValue) ? priceValue : undefined;
    }, [values, priceFieldName]);

    const newPriceRangeFromRequest = useMemo(() => {
        if (!currentFiltersData) {
            return savedPriceRangeRef.current;
        }

        const minPrice = currentFiltersData.min_price;
        const maxPrice = currentFiltersData.max_price;

        if (minPrice !== undefined && maxPrice !== undefined) {
            const newRange = {
                min: fromKopecksToRouble(minPrice),
                max: fromKopecksToRouble(maxPrice),
            };

            savedPriceRangeRef.current = newRange;

            return newRange;
        }

        return savedPriceRangeRef.current;
    }, [currentFiltersData?.min_price, currentFiltersData?.max_price]);

    const currentPriceRange = useMemo(() => {
        if (!isOpen) {
            return priceRange;
        }

        return newPriceRangeFromRequest || priceRange;
    }, [isOpen, priceRange, newPriceRangeFromRequest, formKey]);

    const currentRangeDefault = useMemo(() => {
        return currentPriceRange ? [currentPriceRange.min, currentPriceRange.max] : rangeDefault;
    }, [currentPriceRange, rangeDefault]);

    useEffect(() => {
        if (newPriceRangeFromRequest && currentPriceRange) {
            const priceRangeChanged =
                currentPriceRange.min !== newPriceRangeFromRequest.min ||
                currentPriceRange.max !== newPriceRangeFromRequest.max;

            if (priceRangeChanged) {
                if (!rememberedPrice && currentPriceValue) {
                    const [min, max] = currentPriceValue;
                    const newRangeDefault = [newPriceRangeFromRequest.min, newPriceRangeFromRequest.max];
                    if (
                        isPriceRangeDefault(min, max, newPriceRangeFromRequest) &&
                        (min !== newPriceRangeFromRequest.min || max !== newPriceRangeFromRequest.max)
                    ) {
                        setFieldValue(priceFieldName, newRangeDefault);
                    }
                }
            }
        }
    }, [
        newPriceRangeFromRequest,
        currentPriceRange,
        rememberedPrice,
        currentPriceValue,
        priceFieldName,
        setFieldValue,
    ]);

    return {
        currentPriceRange,
        currentRangeDefault,
        newPriceRangeFromRequest,
    };
};
